import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "redux/store";
import { getLogo } from "../../../util/tenantConfig";
import { getTenantName, isSharedDeployment } from "../../../util";

/**
 * The tenant's logo in the app header — or its name when there is no usable
 * logo.
 *
 * The name fallback exists because of what this rendered before it: the `<img>`
 * was mounted whenever a logo URL was configured, and a URL that fails to load
 * left the browser showing the alt text — the literal word **"Logo"** — at the
 * top of the page. A tenant with a broken logo URL therefore looked like a
 * tenant whose branding was a placeholder (staging, 2026-08-20). An absent URL
 * was quieter but no better: it rendered nothing at all.
 *
 * So: try the logo, and on load failure (or no logo) show the tenant's name.
 *
 * WHICH name is a known compromise. The public config carries `slug`
 * (`innodom` — URL-safe, lowercase) and `organizationName` (the operator's own
 * name, e.g. "Med Facilities GmbH"). Neither is the app-facing name the PO
 * wants here ("InnoDom"), because no such field exists yet — the app has no
 * display name distinct from the legal one, the same distinction organizations
 * already have via `display_name` vs `legal_name`. `organizationName` is used
 * meanwhile because it is a real name rather than a URL fragment; the proper
 * field is tracked in #2102.
 */
/** Hand-tuned caps for the pinned legacy builds, each set against that
 *  tenant's own logo file. Left exactly as they were. */
const pinnedLogoClasses = (tenantName: string | undefined): string => {
    if (tenantName === "dhu") return "max-h-[15px]";
    if (tenantName === "kolula-key") return "max-h-12 max-w-64";
    return "max-h-8 max-w-40";
};

/** Self-service tenants: a sized band rather than a cap — see the note at the
 *  call site for why a cap alone sized nothing. */
const SHARED_LOGO_CLASSES = "h-14 w-auto max-w-52 object-contain";

const TenantLogo = (props: React.ComponentProps<"img">) => {
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const tenantName = getTenantName();

  const organizationName = useSelector(
    (state: RootState) => state.tenantConfig.config.organizationName
  );

  useEffect(() => {
    const fetchAssets = async () => {
      const logo = await getLogo();
      if (typeof logo === "string") {
        setLogo(logo);
        setLogoFailed(false);
      } else {
        setLogo(null);
      }
    };
    fetchAssets();
  }, []);

  if (logo && !logoFailed) {
    return (
      <img
        src={logo}
        // Self-service tenants get a SIZED box; the pinned builds keep their
        // hand-tuned caps.
        //
        // The classes here were `max-h-*`/`max-w-*` only — caps, never a
        // target. An `<img>` with nothing but maxima renders at its INTRINSIC
        // size, so the header did not size logos at all: how big a tenant's
        // logo appeared was decided by the pixel dimensions of whatever file
        // they happened to upload. KGSt-Forum's is 108×78 device pixels, which
        // is 36×26 CSS px on a 3× phone — well inside the old 32×160 cap, so
        // raising that cap would have changed nothing. innodom's stacked logo
        // has the same shape and the same result.
        //
        // `h-14 … object-contain` makes the band the constant instead: every
        // self-service tenant's logo fills the same 56px height, a wide one
        // stops at 208px and letterboxes rather than crowding the header
        // (inside the booking dialog "Zurück" and the close button take ~138px
        // of a 390px viewport, leaving ~252px clear).
        //
        // It also means the upload's resolution now decides SHARPNESS rather
        // than size. A small file is upscaled and looks soft — worth saying in
        // the account-page guidance, which does not mention logo dimensions at
        // all today.
        //
        // Deliberately scoped to the shared deployment: the eight pinned
        // legacy tenants were tuned against their own logos and are left
        // exactly as they are.
        className={`block ${
          isSharedDeployment()
            ? SHARED_LOGO_CLASSES
            : pinnedLogoClasses(tenantName)
        }`}
        {...props}
        // Empty alt, not "Logo": the image is decorative here — the header is
        // not the only place the tenant is named — and an empty alt is what
        // stops a failed load from printing a word at the top of the page.
        alt={""}
        onError={() => setLogoFailed(true)}
      />
    );
  }

  // No logo, or one that did not load. A name is a better answer than a gap.
  if (!organizationName) return null;

  return (
    <span className={"block text-base font-semibold text-foreground truncate max-w-40"}>
      {organizationName}
    </span>
  );
};

export default TenantLogo;
